import { computed, signal, Signal, WritableSignal } from '@angular/core';

import { Address } from '@sondermind/utilities/models-location';
import { AcceptingClientsLastUpdated, SchedulingProfile } from '@sondermind/utilities/models-scheduling';
import { Flags } from '@sondermind/utilities/services';

import { ProfileTabs } from '../enums/profile-tabs.enum';

import { IProfileErrorTabs } from './profile-error-tabs.interface';

export class ProviderUser {
  id = 0;
  fullName: string;
  firstName: string;
  middleName: string;
  lastName: string;
  displayName: string;
  preferredFirstName: string;
  pronouns: string;
  email: string;
  username: string;
  timeZone: string;
  bio: string;
  practiceName: string;
  birthDate: string;
  searchBio: string;
  website: string;
  mailingAddress: Address;
  billingAddress?: Address;
  profilePictureId: string;
  inviteCode: string;
  phoneNumber: string;
  npi: string;
  practiceState: string;
  matchingEnabled: boolean;
  trainingComplete: boolean;
  searchableProfile: boolean;
  // submit_profile, approve_profile, reject_profile
  profileState: string;
  profileRejectMessage: string;
  accountState: string;
  publicPhoneNumber: string;
  password: string;
  passwordConfirmation: string;
  profilePictureThumbnail: string;
  uploadFileName: string;
  uploadFileContents: string;
  smmaAgreement: boolean;
  baaAgreement: boolean;
  hasUnsignedAgreements: boolean;
  unsignedAgreements: any;
  hasPaymentInfo: boolean;
  subscriptionSms: boolean;
  cellphoneNumber: string;
  genderId: number;
  profileErrors: string[];
  profileErrorTabs: IProfileErrorTabs[];
  communityPortalUrl: string;
  canAccessCalendar: boolean;
  canAccessSuperbill: boolean;
  canSubmitSuperbill: boolean;
  hasAnyProviderNetworkCompany: boolean;
  features: any = {};
  hasLicense: boolean;
  certifications: string;
  certificationStates: string;
  acceptingClients: boolean;
  autoSaveSessionNotes: boolean;
  canAcceptNewClients: any = {};
  needPasswordChange = false;
  passwordChangedAt: any;
  activeCardExpired = false;
  noPhysicalLocation: boolean;
  sessionTypesOffered: string[];
  initials: string;
  experimentationKey: string;
  psychiatrist: boolean;
  ePrescriptionEnabled: boolean;
  hasDueInvoices: boolean;
  personaProviderId: number;
  personaAdminId: number;
  platformIdentity: string;
  schedulingProfile: SchedulingProfile;
  acceptingClientsLastUpdatedByType?: AcceptingClientsLastUpdated;
  readonly saasEnabled: WritableSignal<boolean> = signal(false);
  /**
   * If true, this means that a provider is using SonderMind as a SAAS platform.
   * SAAS providers have a significantly different experience to normal providers.
   * This allows us to control their experience and hide certain features.
   * Most notable is that SAAS providers do not have matches.
   * Their only clients are PSC (provider-sourced, manual).
   * As a result, they also have no need for a profile or PSL.
   * Finally, SAAS providers cannot accept insurance.
   */
  readonly isSaasEnabled: Signal<boolean> = computed(() => this.saasEnabled());

  constructor() {
    // set default timezone
    try {
      this.timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone || 'America/Denver';
    } catch {
      this.timeZone = 'America/Denver';
    }
  }

  get loaded(): boolean {
    return this.id !== 0;
  }

  featureEnabled(name: string): boolean {
    return this.features[name];
  }

  featureAsBoolean(name: string): boolean {
    return this.featureAsString(name) === 'true';
  }

  featureAsString(name: string): string {
    const f = this.features[name];
    if (f) {
      return f.current_value;
    }
    return null;
  }

  featureAsNumber(name: string): number {
    return Number.parseFloat(this.featureAsString(name));
  }

  featuresAsFlags(): Flags {
    const flags: Flags = {};
    Object.keys(this.features).forEach((key) => {
      flags[key] = this.featureAsBoolean(key);
    });

    return flags;
  }

  get isProvider(): boolean {
    return this.personaProviderId != null;
  }

  get canAccessChat(): boolean {
    return this.isProvider;
  }

  get canAccessReferrals(): boolean {
    return this.isProvider;
  }

  load_from_json(json: any): ProviderUser {
    this.id = json.id;
    this.fullName = json.fullName;
    this.firstName = json.firstName;
    this.middleName = json.middleName;
    this.lastName = json.lastName;
    this.displayName = json.displayName;

    // this field will be used to edit the preferred first name value, which is then
    // used on the BE to compute displayName. It should not be displayed except to edit.
    this.preferredFirstName = json.preferredFirstName;

    this.pronouns = json.pronouns;
    this.email = json.email;
    this.username = json.username;
    this.timeZone = json.timeZone;
    this.bio = json.bio;
    this.practiceName = json.practiceName;
    this.birthDate = json.birthDate;
    this.searchBio = json.searchBio;
    this.website = json.website;
    this.mailingAddress = json.mailingAddress;
    this.profilePictureId = json.profilePictureId;
    this.inviteCode = json.inviteCode;
    this.phoneNumber = json.phoneNumber;
    this.npi = json.npi;
    this.practiceState = json.practiceState;
    this.matchingEnabled = json.matchingEnabled;
    this.trainingComplete = json.trainingComplete;
    this.searchableProfile = json.searchableProfile;
    this.profileState = json.profileState;
    this.profileRejectMessage = json.profileRejectMessage;
    this.accountState = json.accountState;
    this.publicPhoneNumber = json.publicPhoneNumber;
    this.profilePictureThumbnail = json.profilePictureThumbnail;
    this.hasUnsignedAgreements = json.hasUnsignedAgreements;
    this.unsignedAgreements = json.unsignedAgreements;
    this.hasPaymentInfo = json.hasPaymentInfo;
    this.subscriptionSms = json.subscriptionSms;
    this.cellphoneNumber = json.cellphoneNumber;
    this.genderId = json.genderId;
    this.profileErrors = json.profileErrors;
    this.features = json.features;
    this.communityPortalUrl = json.communityPortalUrl;
    this.canAccessCalendar = json.canAccessCalendar;
    this.hasLicense = json.hasLicense;
    this.certifications = json.certifications;
    this.certificationStates = json.certificationStates;
    this.needPasswordChange = json.needPasswordChange;
    this.passwordChangedAt = new Date(json.passwordChangedAt);
    this.activeCardExpired = json.activeCardExpired;
    this.hasAnyProviderNetworkCompany = json.hasAnyProviderNetworkCompany;
    this.canAccessSuperbill = json.canAccessSuperbill;
    this.canSubmitSuperbill = json.canSubmitSuperbill;
    this.hasDueInvoices = json.hasDueInvoices;

    this.personaAdminId = json.personaAdminId;
    this.personaProviderId = json.personaProviderId;
    this.platformIdentity = json.providerPlatformIdentity;

    this.acceptingClients = json.acceptingClients;
    this.canAcceptNewClients = json.canAcceptNewClients;
    this.autoSaveSessionNotes = json.autoSaveSessionNotes;
    this.schedulingProfile = new SchedulingProfile(json.schedulingProfile);
    this.noPhysicalLocation = json.noPhysicalLocation;
    this.sessionTypesOffered = json.sessionTypesOffered;
    this.experimentationKey = json.experimentationKey;
    this.psychiatrist = json.psychiatrist;
    this.ePrescriptionEnabled = json.ePrescriptionEnabled;
    this.acceptingClientsLastUpdatedByType = json.acceptingClientsLastUpdatedByType;
    this.saasEnabled.set(json.saasEnabled);

    if (json.billingAddress) {
      this.billingAddress = json.billingAddress;
    }

    this.profileErrorTabs = this.getErrorTabs();

    return this;
  }

  /**
   * This method match the error with profileErrors according to the tabs on profile page.
   * If error exists in profileErrors then it will push some info aboout the tab to an array
   *
   * @return {IProfileErrorTabs[]}
   */
  private getErrorTabs(): IProfileErrorTabs[] {
    const errors: IProfileErrorTabs[] = [];
    if (this.profileErrors && this.profileErrors.length > 0) {
      // Tab wise object of errors and the tab info that need to return
      const tabWiseErrorsDetails = [
        {
          errors: [
            'Add a profile image',
            'Enter your first name',
            'Enter public phone number',
          ],
          tabInfo: { name: ProfileTabs.ABOUT_YOU, link: '/profile', dataTest: 'general-tab-link' }
        },
        {
          errors: ['Enter at least one credential'],
          tabInfo: { name: ProfileTabs.CREDENTIALS, link: '/profile/credentials', dataTest: 'credentials-tab-link' }
        },
        {
          errors: [
            'Enter a mini bio',
            'Enter a full bio'
          ],
          tabInfo: { name: ProfileTabs.YOUR_BIO, link: '/profile/bio', dataTest: 'bio-tab-link' }
        },
        {
          errors: [
            'Enter languages spoken',
            'Enter at least one service offering'
          ],
          tabInfo: { name: ProfileTabs.CLIENT_FOCUS, link: '/profile/focus', dataTest: 'client-focus-tab-link' }
        },
        {
          errors: ['Select at least one top specialty'],
          tabInfo: { name: ProfileTabs.SPECIALTIES, link: '/profile/specialties', dataTest: 'specialties-tab-link' }
        },
        {
          errors: [],
          tabInfo: { name: ProfileTabs.APPROACHES, link: '/profile/approaches', dataTest: 'approaches-tab-link' }
        },
        {
          errors: [
            'Enter at least one race/ethnicity',
            'Select your gender',
            'Select option for "Do you identify as LGBTQIA+?"'
          ],
          tabInfo: { name: ProfileTabs.IDENTITY, link: '/profile/identity', dataTest: 'identity-tab-link' }
        }
      ];

      // Check for errors
      tabWiseErrorsDetails.forEach((obj) => {
        if (obj.errors.some((e) => this.profileErrors.includes(e))) {
          errors.push(obj.tabInfo);
        }
      });
    }

    return errors;
  }
}
